import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

export const TYPE_COLORS = [
  "#C86D51", "#4F46E5", "#0D9488", "#D97706", "#DB2777",
  "#7C3AED", "#2563EB", "#65A30D", "#0891B2", "#DC2626",
];

export function typeColor(name) {
  let h = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return TYPE_COLORS[h % TYPE_COLORS.length];
}

const RAD = Math.PI / 180;

function segmentLabel(mode) {
  return ({ cx, cy, midAngle, innerRadius, outerRadius, value, payload }) => {
    if (!payload || payload.pct < 6) return null;
    const r = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
        fontSize={13} fontWeight={800} style={{ pointerEvents: "none" }}>
        {mode === "pct" ? `${payload.pct}%` : value}
      </text>
    );
  };
}

export function ProductTypeDonut({ data, testid = "product-type-donut" }) {
  const [mode, setMode] = useState("pct");
  const rows = (data?.breakdown || []).filter((r) => r.received_qty > 0);
  const total = data?.total_received || 0;
  const chartData = rows.map((r) => ({ name: r.product_type, value: r.received_qty, pct: r.pct }));

  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4 gap-2">
        <h3 className="font-display text-base font-bold text-slate-900">Product Type Production</h3>
        <div className="flex rounded-full bg-slate-100 p-1 flex-shrink-0">
          <button data-testid="donut-mode-pct" onClick={() => setMode("pct")}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${mode === "pct" ? "copper-gradient text-white shadow" : "text-slate-500"}`}>%</button>
          <button data-testid="donut-mode-qty" onClick={() => setMode("qty")}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${mode === "qty" ? "copper-gradient text-white shadow" : "text-slate-500"}`}>Pcs</button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-10">No completed production this month.</p>
      ) : (
        <div className="flex flex-col items-center gap-6 md:flex-row md:gap-8">
          <div className="relative h-64 w-64 md:h-80 md:w-80 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData} dataKey="value" nameKey="name"
                  innerRadius="62%" outerRadius="92%" paddingAngle={2} cornerRadius={4}
                  strokeWidth={0} label={segmentLabel(mode)} labelLine={false}>
                  {chartData.map((d) => <Cell key={d.name} fill={typeColor(d.name)} />)}
                </Pie>
                <Tooltip
                  formatter={(v, n, item) => [
                    mode === "pct" ? `${item.payload.pct}% (${Number(v).toLocaleString()} pcs)` : `${Number(v).toLocaleString()} pcs (${item.payload.pct}%)`,
                    n,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Received</p>
              <p className="tnum text-3xl font-black text-slate-900 leading-tight">{total.toLocaleString()}</p>
              <p className="text-[10px] font-bold uppercase text-slate-400">pcs</p>
            </div>
          </div>

          <div className="w-full flex-1 space-y-3">
            {rows.map((r) => (
              <div key={r.product_type} data-testid={`donut-legend-${r.product_type}`} className="flex items-center gap-3">
                <span className="h-3.5 w-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: typeColor(r.product_type) }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{r.product_type}</p>
                  <p className="text-xs text-slate-400 tnum">
                    {mode === "pct" ? `${r.received_qty.toLocaleString()} pcs` : `${r.pct}%`}
                  </p>
                </div>
                <p className="tnum text-lg font-black text-slate-900">
                  {mode === "pct" ? `${r.pct}%` : r.received_qty.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
